import { useAnswers } from "../funnel/answers";
import { draftTypo, useDraft } from "../funnel/draft";
import { SaveNote, useStepSave } from "../funnel/stepSave";
import { useFunnelNav } from "../funnel/useFunnelNav";
import { useParticipation } from "../funnel/useParticipation";
import { useTypologyWord } from "../funnel/wording";
import { fallbackOf } from "../settings/catalog";
import { useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * REGISTRATION — THE PARTICIPANT'S PROFILE.
 *
 * ⚠️ THE ANSWER IS NOT THE ORGANISER'S READING SCOPE. In the product, a
 * participant's typology drives the cascade they are served — it outranks the
 * language — and this step will set it on THEM once their identity for this event
 * can be resolved. What it must NOT touch is the "as" selector in the organiser
 * bar: wired to it, a click meant as a participant silently moved which typology
 * THE PANEL WRITES TO, and an organiser previewing "every participant" would have
 * been editing an exhibitor's overrides without a word. So the choice is kept with
 * the other answers (`funnel/answers.ts`) and the reading scope stays the
 * organiser's alone.
 *
 * ⚠️ THE CHOICES ARE THE EVENT'S OWN TYPOLOGIES, read from its route — not a fixed
 * list. The prototype hard-codes three because it has no database; here an event
 * with seven profiles offers seven, and one with none does not show this step at
 * all (see `funnel/thread.ts`).
 *
 * A typology taken out of the choice stays reachable through its invitation link:
 * it disappears from this list, not from the event.
 *
 * Displayed strings are French: this is a French product.
 */
const ICI = "/registration/typology";

export function Typology({ asPage }: { asPage?: true }) {
  const t = useT();
  const { value, typos } = useSettings();
  // What THIS event calls a profile. Written by hand here, the same notion ended up
  // with two words two lines apart — see `funnel/wording.ts`.
  const word = useTypologyWord();

  const title = useText(
    "FO_MEETING_V2_TYPO_TITLE",
    fallbackOf("FO_MEETING_V2_TYPO_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_TYPO_SUBTITLE",
    fallbackOf("FO_MEETING_V2_TYPO_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_TYPO_HTML");

  // What the earlier steps answered — forwarded by `go`, or the confirmation would
  // lose the participation answer on the way through here and greet somebody who
  // declined. The profile is recorded into THIS entry, so Back restores it.
  const [answers, record] = useAnswers();
  // What the server already holds, when nothing has been answered this time —
  // the same three-source order as step 1.
  const { me, recorded } = useParticipation();
  // Et entre les deux, le profil choisi lors d'un premier parcours abandonné (voir
  // `funnel/draft.ts`).
  const { draft, keep } = useDraft(me, recorded);

  // Offered unless somebody has explicitly taken it out — the prototype hides on
  // a `false`, never on an absent key. The `null` typology is not a choice: it is
  // the ABSENCE of one, the level everything else falls back to.
  /**
   * ⚠️ THE NOTE COMES FROM THE SETTINGS, NOT FROM THE EVENT. A typology row carries a
   * LABEL and nothing else — no description, no icon, no colour. Yet real events name
   * them in trade words ("MARQUES", "DETAILLANT", "TOURISME"), and nobody from outside
   * knows which one is theirs. The sentence that says so has to be written somewhere,
   * and the only place the organiser has is their own configuration.
   */
  const note = (id: number) => {
    const written = value(`FO_MEETING_V2_TYPO_NOTE_${id}`);
    return typeof written === "string" ? written.trim() : "";
  };

  const offered = typos.flatMap((typo) =>
    typo.id !== null && value(`FO_MEETING_V2_TYPO_SHOW_${typo.id}`) !== false
      ? [{ id: typo.id, name: typo.name, note: note(typo.id) }]
      : [],
  );
  const picked =
    answers.typoId ??
    draftTypo(
      draft,
      offered.map((typo) => typo.id),
    ) ??
    recorded?.typoId ??
    null;

  const { previous, next, go } = useFunnelNav(ICI);
  const { ready, state, save, touched, removal } = useStepSave({ asPage });

  return (
    <section className="page">
      <div className="ttl" data-cfg="typoTitle">
        <InPlace settingKey="FO_MEETING_V2_TYPO_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_TYPO_SUBTITLE" text={subtitle} as="p" />
      </div>

      {/* Le bloc libre de l'organisateur, sous le titre : son HTML, assaini comme
          partout (`ui/Prose`). Vide, rien ne s'affiche. */}
      {html.trim() !== "" && (
        <Prose html={html} className="se-prose" data-cfg="typoTitle" />
      )}

      {/* Every typology taken out of the choice. Said, rather than left as an
          empty list the participant cannot get past. */}
      <div
        className="alert"
        data-cfg="typoOptions"
        hidden={offered.length > 0}
      >
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>Aucune réponse proposée</b>
          <small>
            Réactivez au moins une valeur de « {word} », ou retirez cette étape du
            parcours.
          </small>
        </span>
      </div>

      {/**
       * ═══ ONE PROFILE PER LINE, THE MARKER ON THE LEFT ═══
       *
       * Nothing to declare here any more: every choice list in the funnel is stacked,
       * on a phone and on a wide screen alike. The desktop sheet says why the
       * prototype's row was dropped — measured on these very profiles.
       */}
      <ul className="opts" data-cfg="typoOptions">
        {offered.map((typo) => (
          <li key={typo.id}>
            <button
              type="button"
              className={`opt${picked === typo.id ? " on" : ""}`}
              aria-pressed={picked === typo.id}
              onClick={() => {
                // « C'est enregistré » ne doit pas rester affiché au-dessus d'un profil
                // qu'on vient de changer depuis.
                touched();
                record({ typoId: typo.id });
                keep({ typoId: typo.id });
              }}
            >
              {/* ⚠️ UN ROND, PARCE QU'ON N'EN RETIENT QU'UN — la règle du tunnel (voir
                  `funnel.css`). Cette étape n'avait AUCUN marqueur : elle était la
                  seule liste de choix où rien ne marquait la ligne retenue autrement
                  que par sa bordure, et où l'alignement du texte ne suivait pas celui
                  des étapes voisines.

                  ⚠️ ET TOUJOURS PAS D'ICÔNE, contrairement à la participation. Chaque
                  profil porterait le MÊME dessin : il ne distinguerait rien, et
                  prendrait la place où l'œil se pose en premier. Les réponses de
                  participation gardent les leurs parce qu'elles diffèrent — là, le
                  dessin EST la différence. */}
              <span className="opt__r" aria-hidden="true">
                {picked === typo.id && <Icon name="check" />}
              </span>
              {/* La même paire que partout ailleurs dans le tunnel : le nom, et sous lui
                  ce qui l'explique. Le second n'apparaît que s'il a été écrit — un
                  « Étudiant » se passe très bien de commentaire. */}
              <span className="opt__t">
                <b>{typo.name}</b>
                {typo.note && <small>{typo.note}</small>}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {/**
       * ⚠️ CE QUE LE BOUTON PÂLE NE DIT PAS. « Continuer » reste désactivé tant que rien
       * n'est choisi — c'est juste, mais un bouton pâle se lit comme une panne autant
       * que comme une attente. La phrase dit laquelle des deux, et disparaît dès qu'elle
       * n'a plus d'objet.
       *
       * ⚠️ SANS NOMMER LA NOTION. Le mot de l'événement pour un profil est réglable
       * (« Typologie », « Profil », « Vous êtes ») et n'est pas accordé : l'insérer dans
       * une phrase donnerait « Choisissez Typologie pour continuer ». Le voisin, lui, le
       * cite entre guillemets parce qu'il s'adresse à l'organisateur.
       */}
      {offered.length > 0 && picked === null && (
        <p className="hint">{t("PICK_AN_ANSWER_TO_CONTINUE", 'Choisissez une réponse pour continuer.')}</p>
      )}

      <SaveNote state={state} removal={removal} />

      {/* Dans le tunnel « Continuer », en page « Enregistrer », jamais les deux. Voir
          l'étape dates, où la règle est écrite. */}
      <div className="actions">
        {asPage && (
          <Button type="button" variant="ghost" onClick={() => go("/")}>
            {t("BACK_TO_HOME", "Retour à l'accueil")}
          </Button>
        )}
        {!asPage && previous && (
          <Button type="button" variant="ghost" onClick={() => go(previous.path)}>
            {t("BACK", "Retour")}
          </Button>
        )}
        {asPage && (
          <Button
            type="button"
            data-cfg="typoButton"
            loading={state === "saving"}
            disabled={picked === null || !ready}
            onClick={() => picked !== null && save({ typoId: picked })}
          >
            {t("SAVE", "Enregistrer")}
          </Button>
        )}
        {!asPage && (
          <Button
            type="button"
            data-cfg="typoButton"
            /* Nothing chosen, nowhere to go: a participant sent onward without a
               profile would read the whole rest of the site through the wrong
               configuration. */
            disabled={picked === null || !next}
            /* Le profil affiché part avec : repris d'un brouillon, il n'est pas encore
               dans l'état de navigation. */
            onClick={() =>
              next && go(next.path, picked !== null ? { typoId: picked } : {})
            }
          >
            {t("CONTINUE", "Continuer")}
          </Button>
        )}
      </div>
    </section>
  );
}
